<template>
  <Teleport :disabled="teleport == false" :to="teleportLocation">
    <div>
      <dialog
        class="rds-modal-wrapper py-0 border-0 position-unset h-content"
        tabindex="-1"
        ref="dialog"
        :id="id"
        @close="handleModalClose"
        :class="[dialogClasses, { 'dropin-animation-wrapper': visible }]">
        <div
          class="modal d-block m-auto position-relative"
          :class="[modalClasses, { 'dropin-animation': visible }]">
          <div
            class="rds-modal-dialog modal-dialog my-auto mx-auto"
            :class="modalDialogClasses"
            ref="modalContent">
            <div
              class="modal-content border-0 position-relative"
              :class="modalContentClasses">
              <div
                v-if="exteriorCloseBtn && useCustomClose == false"
                class="exterior-close-wrapper d-flex">
                <button
                  class="rds-modal-close exterior mb-space-xs bg-white text-dark-1 d-flex justify-content-center align-items-center text-center"
                  @click="handleModalCloseButtonClick($event)"
                  aria-label="Close">
                  <span>×</span>
                </button>
              </div>
              <div v-if="displayModalHeader" class="modal-header border-0">
                <button
                  v-if="
                    !exteriorCloseBtn &&
                    displayModalHeader &&
                    useCustomClose == false
                  "
                  class="rds-modal-close"
                  @click="handleModalCloseButtonClick($event)"
                  aria-label="Close">
                  ×
                </button>
                <slot name="header">
                  <h2 v-if="headerText" class="h2-small modal-title">
                    {{ headerText }}
                  </h2>
                </slot>
              </div>
              <div class="d-flex flex-column mw-100 vw-100">
                <div
                  class="d-flex justify-content-between flex-row-reverse align-items-start">
                  <button
                    v-if="
                      !exteriorCloseBtn &&
                      !displayModalHeader &&
                      useCustomClose == false
                    "
                    class="rds-modal-close p-space-xs"
                    @click="handleModalCloseButtonClick($event)"
                    aria-label="Close">
                    ×
                  </button>
                  <slot name="title-slot">
                    <h3 v-if="title" class="p-space-xs" :class="titleClasses">
                      {{ title }}
                    </h3>
                  </slot>
                </div>

                <div class="modal-body p-0 border-0">
                  <slot />
                </div>
              </div>
              <div v-if="slots.footer" class="modal-footer border-0">
                <slot name="footer"> </slot>
              </div>
            </div>
          </div>
        </div>
        <div
          v-show="visible"
          class="modal-backdrop"
          :style="backdropStyles"></div>
      </dialog>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { onClickOutside } from "@vueuse/core";
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  useSlots,
  watch,
  type RendererElement,
} from "vue";

interface Props {
  id?: string;
  title?: string;
  titleVariant?: string;
  headerText?: string;
  size?: "sm" | "md" | "lg" | "xl";
  centered?: boolean;
  bgVariant?: string;
  overlayColor?: string;
  overlayOpacity?: number;
  exteriorCloseBtn?: boolean;
  borderRadius?: 0 | 1 | 2 | 3 | 4 | 5;
  useCustomClose?: boolean;
  visible: boolean;
  teleport?: boolean;
  teleportLocation?: string | RendererElement | "body" | "html";
}

const props = withDefaults(defineProps<Props>(), {
  titleVariant: "dark-3",
  bgVariant: "white",
  overlayColor: "var(--rds-dark-3, #909090)",
  overlayOpacity: 0.7,
  useCustomClose: false,
  borderRadius: 1,
  visible: false,
  teleport: true,
  teleportLocation: "body",
});

/** Events */
const emits = defineEmits<{
  /**
   *  Emitted when the modal is opened. No event since it's triggered by watching the `visible` prop.
   */
  (e: "open"): void;
  /**
   *  Emitted when the modal is closed by clicking the "x".
   */
  (e: "close", event: Event): void;
  /**
   *  Emitted right when the modal is closed. passes along if the modal was closed using a close button click.
   */
  (e: "hidden", event: Event): void;
}>();

const slots = useSlots();
const dialog = ref<HTMLDialogElement | null>(null);
const modalContent = ref<HTMLElement | null>(null);

const getColor = (color: string) => {
  if (color.includes("var(")) {
    return `${color}`;
  } else if (color.includes("--")) {
    return `var(${color})`;
  } else {
    return color;
  }
};

const openDialog = () => {
  if (dialog.value && typeof dialog.value.showModal === "function") {
    dialog.value.showModal();
    emits("open");
  }
};

onClickOutside(modalContent, () => {
  if (props.visible) {
    closeModal();
  }
});

const handleModalClose = (event: Event) => {
  emits("hidden", event);
};

const handleModalCloseButtonClick = (event: Event) => {
  dialog.value && typeof dialog.value.close === "function"
    ? dialog.value.close()
    : null;
  emits("close", event);
  closeModal();
};

const closeModal = () => {
  dialog.value && typeof dialog.value.close === "function"
    ? dialog.value.close()
    : null;
};

const displayModalHeader = computed(() => {
  if (slots.header || props.headerText) {
    return true;
  }
  return false;
});

const dialogClasses = computed(() => {
  let classes = "";
  props.exteriorCloseBtn
    ? (classes += ` px-sm-space-md  py-sm-0 overflow-y-clip overflow-x-visible `)
    : "";
  props.centered ? "my-auto" : (classes += " mt-space-sm ");
  return classes;
});

const backdropStyles = computed(() => {
  let styles = "";
  styles += `--rds-backdrop-bg: ${getColor(props.overlayColor)};`;
  styles += `--rds-backdrop-opacity: ${props.overlayOpacity}`;
  return styles;
});

const modalClasses = computed(() => {
  return props.exteriorCloseBtn
    ? `mh-95-vh overflow-x-visible pe-md-space-md`
    : "";
});

const modalDialogClasses = computed(() => {
  let classes = "";
  classes += ` modal-${props.size}`;
  props.centered ? (classes += ` modal-dialog-centered `) : null;
  props.exteriorCloseBtn
    ? (classes += ` w-100-sub-space `)
    : (classes += ` w-100 `);
  return classes;
});

const modalContentClasses = computed(() => {
  let classes = "";
  props.exteriorCloseBtn ? (classes += ` mt-space-md `) : "";
  classes += `rounded-${props.borderRadius} bg-${props.bgVariant}`;
  return classes;
});

const titleClasses = computed(() => {
  let classes = "";
  classes += `text-${props.titleVariant}`;
  return classes;
});

watch(
  () => props.visible,
  (newValue) => {
    if (newValue) {
      openDialog();
    } else {
      dialog.value && typeof dialog.value.close === "function"
        ? dialog.value.close()
        : null;
    }
  }
);
onMounted(() => {
  if (props.visible) {
    openDialog();
  }
});
onBeforeUnmount(() => {
  dialog.value && typeof dialog.value.close === "function"
    ? dialog.value.close()
    : null;
});
</script>

<style lang="scss" scoped>
.mh-95-vh {
  max-height: 95vh;
}

dialog::backdrop {
  background: transparent;
}
.modal-backdrop {
  opacity: var(--rds-backdrop-opacity, 0.7);
  z-index: 0;
  pointer-events: none;
}

.rds-modal-wrapper {
  --dropin-animation-timing: 0.25s;
  background-color: transparent;
  position: fixed;
  height: 100vw;
}

.modal-content {
  --rds-modal-border-color: #fff;
  --rds-modal-header-border-color: #fff;
  --rds-modal-footer-border-color: #fff;
}

dialog {
  background: transparent;
}
.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-direction: row-reverse;
}

button.rds-modal-close {
  padding: 0;
  text-decoration: none;
  font-weight: 700;
  user-select: none;
  background-color: transparent;
  border: none;
}

.rds-modal-close {
  z-index: 1;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
  color: #000;
  opacity: 0.5;
}

.rds-modal-close.exterior {
  margin-left: auto;
  margin-right: 0;
  border-radius: 50%;
  height: 24px;
  width: 24px;
  opacity: 1;
}

.rds-modal-close:hover {
  opacity: 1;
}

.exterior-close-wrapper {
  position: absolute;
  right: 0;
  top: -2rem;
}
.w-100-sub-space {
  width: 100%;
}

@media (min-width: 575px) {
  .overflow-y-clip {
    overflow-y: clip;
  }
}

@media (min-width: 768px) {
  .exterior-close-wrapper {
    right: -2rem;
    top: 0;
  }
  .w-100-sub-space {
    width: calc(100% - 2rem);
  }
}

// Dropin animation and animation to prevent scrollbar from popping up during animation
.dropin-animation-wrapper {
  animation: dropin-wrapper var(--dropin-animation-timing);
}
.dropin-animation {
  animation: dropin var(--dropin-animation-timing)
    cubic-bezier(0.36, 0.07, 0.19, 0.97);
  transform: translate3d(0, 0, 0);
}
@keyframes dropin-wrapper {
  0% {
    overflow-y: hidden;
  }
  100% {
    overflow-y: hidden;
  }
}
@keyframes dropin {
  0% {
    transform: translate3d(0, -40px, 0);
    opacity: 0;
  }
  60% {
    transform: translate3d(0, 10px, 0);
  }
  100% {
    opacity: 1;
    transform: translate3d(0, 0px, 0);
  }
}
</style>
